import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { AllCommunityModule, ModuleRegistry, DateFilterModule } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { toast, ToastContainer } from 'react-toastify';
import axiosInstance from '../../../../lib/axios';
import { Box, Typography, CircularProgress, Tooltip, IconButton, Button, Chip, Dialog } from '@mui/material';
import { usePermissions } from '../../../../GlobalFunction/userPermissions';
import { useSelector, useDispatch } from 'react-redux'; // Add useDispatch here
import { selectGlobalProperty, setGlobalProperty } from '../../../../redux/propertySlice'; // Add setGlobalProperty import
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import '../../../../AgGridStyle.css';
import HomeIcon from '@mui/icons-material/Home';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TiffinDashboard from './TiffinDashboard.js';
import AssessmentIcon from '@mui/icons-material/Assessment';

ModuleRegistry.registerModules([AllCommunityModule, DateFilterModule]);

const TiffinTable = () => {
    const [rowData, setRowData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const UserId = sessionStorage?.getItem('UserId');
    const permissions = usePermissions("Tiffin Service");
    const { AddRec = "0" } = permissions || {};
    const gridRef = useRef();
    const [showDashboard, setShowDashboard] = useState(false);
    const navigate = useNavigate();
    const globalProperty = useSelector(selectGlobalProperty);
    const dispatch = useDispatch(); // Add dispatch
    const [isPropertyReady, setIsPropertyReady] = useState(false); // Add property ready state

    // Initialize property from localStorage on component mount
    useEffect(() => {
        const initializeProperty = () => {
            const savedPropertyId = localStorage.getItem('selectedPropertyId');
            const isLocked = localStorage.getItem('propertyLocked') === 'true';
            
            console.log('TiffinTable - Initializing property:', { 
                savedPropertyId, 
                isLocked, 
                currentGlobalProperty: globalProperty 
            });
            
            if (savedPropertyId && isLocked && !globalProperty) {
                dispatch(setGlobalProperty(parseInt(savedPropertyId)));
                console.log('TiffinTable - Restored property from localStorage:', savedPropertyId);
            }
            
            setIsPropertyReady(true);
        };
        
        initializeProperty();
    }, [dispatch, globalProperty]);

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const dd = String(date.getDate()).padStart(2, '0');
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const yyyy = date.getFullYear();
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
    };

    // Format time for display
    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        return `${hh}:${min}`;
    };

    // Get status color
    const getStatusColor = (status) => {
        switch(status?.toLowerCase()) {
            case 'booked':
                return '#22c55e';
            case 'pending':
                return '#f59e0b';
            case 'cancelled':
                return '#ef4444';
            case 'delivered':
                return '#3b82f6';
            default:
                return '#6b7280';
        }
    };

    // Get status icon
    const getStatusIcon = (status) => {
        switch(status?.toLowerCase()) {
            case 'booked':
                return <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />;
            case 'pending':
                return <ScheduleIcon sx={{ fontSize: 16, color: '#f59e0b' }} />;
            case 'cancelled':
                return <CancelIcon sx={{ fontSize: 16, color: '#ef4444' }} />;
            default:
                return null;
        }
    };

    const colDefs = useMemo(() => [
        { 
            field: 'RowNum', 
            headerName: 'Sr.No', 
            width: 80,
            valueGetter: (params) => params.node.rowIndex + 1
        },
        { 
            field: 'Tfn_ReqId', 
            headerName: 'Order ID', 
            width: 100,
            filter: 'agNumberColumnFilter'
        },
        { 
            field: 'Req_Date', 
            headerName: 'Order Date', 
            width: 150,
            valueFormatter: (params) => formatDate(params.value),
            filter: 'agDateColumnFilter'
        },
        { 
            field: 'MealTypeName', 
            headerName: 'Meal Type', 
            width: 150,
            cellRenderer: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FastfoodIcon sx={{ fontSize: 16, color: '#635bff' }} />
                    <span>{params.value || 'N/A'}</span>
                </Box>
            )
        },
        { 
            field: 'InstituteName', 
            headerName: 'Institute', 
            width: 200,
            cellRenderer: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{params.value || 'N/A'}</span>
                </Box>
            )
        },
        { 
            field: 'Dlv_Loc', 
            headerName: 'Delivery Location', 
            width: 200,
            cellRenderer: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocationOnIcon sx={{ fontSize: 16, color: '#f97316' }} />
                    <span>{params.value || 'N/A'}</span>
                </Box>
            )
        },
        { 
            field: 'Req_Qty', 
            headerName: 'Qty', 
            width: 100,
            filter: 'agNumberColumnFilter',
            cellStyle: { fontWeight: 'bold', textAlign: 'center' }
        },
        { 
            field: 'Dlv_Time', 
            headerName: 'Delivery Time', 
            width: 120,
            valueFormatter: (params) => formatTime(params.value)
        },
        { 
            field: 'Dlv_OTP', 
            headerName: 'OTP', 
            width: 120,
            cellStyle: { fontFamily: 'monospace', fontWeight: 'bold', fontSize: '14px' }
        },
        { 
            field: 'Tfn_ReqSt', 
            headerName: 'Status', 
            width: 150,
            cellRenderer: (params) => (
                <Chip 
                    size="small"
                    icon={getStatusIcon(params.value)}
                    label={params.value || 'N/A'}
                    sx={{
                        backgroundColor: `${getStatusColor(params.value)}20`,
                        color: getStatusColor(params.value),
                        fontWeight: 600,
                        '& .MuiChip-icon': { color: 'inherit' }
                    }}
                />
            )
        },
        { 
            field: 'Req_Remark', 
            headerName: 'Remarks', 
            width: 200,
            tooltipField: 'Req_Remark'
        },
    ], []);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
        floatingFilter: true,
        width: 100,
        tooltipValueGetter: (params) => params.value ?? '',
    }), []);

    // Fetch tiffin orders
    const fetchTiffinOrders = async () => {
        // Wait for property to be ready
        if (!isPropertyReady) return;
        
        // Check if globalProperty is available
        if (!globalProperty) {
            console.warn('TiffinTable - No global property selected');
            toast.warning('Please select a property first');
            setIsLoading(false);
            return;
        }
        
        if (!UserId) {
            toast.error('User not found. Please login again.');
            return;
        }

        console.log('TiffinTable - Fetching orders with:', { 
            UserId, 
            globalProperty,
            isPropertyReady 
        });

        setIsLoading(true);
        try {
            const response = await axiosInstance.post('TrnTiffin/RetriveTrnTiffin', {
                Id: parseInt(UserId),
                Flag: "Tfn_User",
                PropId: parseInt(globalProperty) 
            });

            if (response.data?.Data && response.data.Data.length > 0) {
                // Fetch additional details for each order
                const ordersWithDetails = await Promise.all(
                    response.data.Data.map(async (order) => {
                        try {
                            // Fetch meal type name
                            const mealTypeRes = await axiosInstance.post('MstFood/GetdrpMstMealType', { Flag: '' });
                            const mealType = mealTypeRes.data?.Data?.find(m => m.MealTypeId === order.MealTypeId);
                            
                            // Fetch institute name
                            const instituteRes = await axiosInstance.post('MstInstitute/getMstInstitutedrp', {});
                            const institute = instituteRes.data?.Data?.find(i => i.Id === order.InstituteId);
                            
                            return {
                                ...order,
                                MealTypeName: mealType?.MealTypeName || 'N/A',
                                InstituteName: institute?.Name || 'N/A'
                            };
                        } catch (err) {
                            console.error('Error fetching details for order:', order.Tfn_ReqId, err);
                            return {
                                ...order,
                                MealTypeName: 'N/A',
                                InstituteName: 'N/A'
                            };
                        }
                    })
                );
                setRowData(ordersWithDetails);
            } else {
                setRowData([]);
                if (response.data?.Data?.length === 0) {
                    toast.info('No tiffin orders found');
                }
            }
        } catch (error) {
            console.error('Error fetching tiffin orders:', error);
            toast.error(error.response?.data?.Message || 'Error fetching tiffin orders');
            setRowData([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch tiffin orders when property is ready
    useEffect(() => {
        if (isPropertyReady && globalProperty) {
            fetchTiffinOrders();
        }
    }, [isPropertyReady, globalProperty]);

    // Fetch single order for view/edit
    const fetchSingleOrder = async (orderId) => {
        try {
            const response = await axiosInstance.post('TrnTiffin/RetriveTrnTiffin', {
                Id: orderId,
                Flag: "R"
            });

            if (response.data?.Data && response.data.Data.length > 0) {
                return response.data.Data[0];
            }
            return null;
        } catch (error) {
            console.error('Error fetching order details:', error);
            toast.error('Error fetching order details');
            return null;
        }
    };

    // Handle view order
    const handleViewOrder = async (order) => {
        try {
            const orderDetails = await fetchSingleOrder(order.Tfn_ReqId);
            if (orderDetails) {
                navigate('/TiffinDelivery', {
                    state: {
                        mode: 'view',
                        orderData: orderDetails
                    }
                });
            }
        } catch (error) {
            console.error('Error viewing order:', error);
            toast.error('Error loading order details');
        }
    };

    // Handle edit order
    const handleEditOrder = async (order) => {
        try {
            const orderDetails = await fetchSingleOrder(order.Tfn_ReqId);
            if (orderDetails) {
                navigate('/TiffinDelivery', {
                    state: {
                        mode: 'edit',
                        orderData: orderDetails
                    }
                });
            }
        } catch (error) {
            console.error('Error editing order:', error);
            toast.error('Error loading order for editing');
        }
    };

    // Handle row double click
    const handleRowDoubleClick = useCallback((params) => {
        handleViewOrder(params.data);
    }, []);

    const handleAddOrder = () => {
        navigate('/TiffinDelivery', {
            state: {
                mode: 'add'
            }
        });
    };

    // Handle clear filters
    const handleClearFilters = () => {
        gridRef.current?.api?.setFilterModel(null);
    };

    // Show loading while property is being initialized
    if (!isPropertyReady) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh' 
            }}>
                <CircularProgress sx={{ color: '#635bff' }} />
                <Typography sx={{ ml: 2 }}>Initializing...</Typography>
            </Box>
        );
    }

    // Show warning if no property selected
    if (!globalProperty) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh',
                flexDirection: 'column',
                gap: 2
            }}>
                <Typography variant="h6" color="error">
                    No Property Selected
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    Please select a property to view tiffin orders.
                </Typography>
                <Button 
                    variant="contained" 
                    onClick={() => navigate('/dashboard')}
                    sx={{ bgcolor: '#635bff' }}
                >
                    Go to Dashboard
                </Button>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                height: '90vh',
                padding: '10px',
                backgroundColor: 'rgb(249, 249, 249)',
                position: 'relative'
            }}
        >
            <ToastContainer />
            <Box
                sx={{
                    backgroundColor: "#ffffff",
                    padding: { xs: "8px 16px", sm: "12px 16px" },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                    borderRadius: "12px",
                    margin: "10px auto",
                    maxWidth: "95%",
                    flexDirection: { xs: "column", sm: "column", md: "row" },
                    gap: 2,
                }}
            >
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator="›"
                    sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "column", md: "row" }
                    }}
                >
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
                        onClick={() => navigate('/dashboard')}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        <Box component="span">Home</Box>
                    </Link>
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500" }}
                        onClick={() => navigate('/food-transport')}
                    >
                        Food & Transport
                    </Link>

                    <Typography color="text.primary" sx={{ fontWeight: "500" }}>
                        Tiffin Orders
                    </Typography>
                </Breadcrumbs>

                {/* Buttons container */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: { xs: 1, sm: 1, md: 0 },
                        flexShrink: 0,
                    }}
                >
                    <Tooltip title='Clear all applied filter' arrow>
                        <Button
                            variant="outlined"
                            onClick={handleClearFilters}
                            size="small"
                            sx={{
                                borderRadius: "50px",
                                textTransform: "none",
                                borderColor: "#635bff",
                                color: "#635bff",
                                "&:hover": {
                                    borderColor: "#524ccc",
                                    backgroundColor: "rgba(99, 91, 255, 0.04)",
                                }
                            }}
                        >
                            Clear Filters
                        </Button>
                    </Tooltip>

                    <Tooltip
                        title={AddRec !== "1" ? "You have no permission to add record" : "Add New Tiffin Order"}
                        placement="top"
                        arrow
                    >
                        <span>
                            <Button
                                variant="contained"
                                onClick={handleAddOrder}
                                size="small"
                                disabled={AddRec !== "1"}
                                sx={{
                                    background: AddRec === "1" ? "#635bff" : "rgba(0, 0, 0, 0.12)",
                                    color: AddRec === "1" ? "white" : "rgba(0, 0, 0, 0.26)",
                                    borderRadius: "50px",
                                    padding: "6px 15px",
                                    boxShadow: AddRec === "1" ? "0px 6px 15px rgba(0, 0, 0, 0.2)" : "none",
                                    "&:hover": {
                                        background: AddRec === "1" ? "#524ccc" : "rgba(0, 0, 0, 0.12)"
                                    }
                                }}
                            >
                                <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> New Order
                            </Button>
                        </span>
                    </Tooltip>

                   <Tooltip title='Dashboard' arrow>
    <IconButton
        onClick={() => navigate('/TiffinDashboard')}
        sx={{
            backgroundColor: '#524ccc',
            borderRadius: '20px',
            px: 1.5,
            py: 0.7,
            display: 'flex',
            alignItems: 'center',
            gap: 0.8,
            '&:hover': {
                backgroundColor: '#524ccc',
            },
            boxShadow: 3,
            color: 'white'
        }}
    >
        <DashboardIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
        <Typography
            sx={{
                fontSize: { xs: '12px', sm: '14px' },
               
                color: 'white',
                lineHeight: 1
            }}
        >
            DASHBOARD
        </Typography>
    </IconButton>
</Tooltip>

                     <Tooltip
                        title='View Reoprt'
                        
                        arrow
                    >
                        <span>
                            <Button
                                variant="contained"
                                onClick={() => navigate('/DeliveryRouteDashboard')}
                                size="small"
                                
                                sx={{
                                    background: AddRec === "1" ? "#635bff" : "rgba(0, 0, 0, 0.12)",
                                    color: AddRec === "1" ? "white" : "rgba(0, 0, 0, 0.26)",
                                    borderRadius: "50px",
                                    padding: "6px 15px",
                                    boxShadow: AddRec === "1" ? "0px 6px 15px rgba(0, 0, 0, 0.2)" : "none",
                                   
                                }}
                            >
                                <AssessmentIcon fontSize="small" sx={{ mr: 0.5 }} /> Report
                            </Button>
                            
                        </span>
                    </Tooltip>

                    {showDashboard && (
                        <Dialog
                            open={showDashboard}
                            onClose={() => setShowDashboard(false)}
                            fullScreen
                            PaperProps={{
                                sx: {
                                    bgcolor: 'transparent',
                                    boxShadow: 'none',
                                    overflow: 'hidden'
                                }
                            }}
                        >
                            <TiffinDashboard onClose={() => setShowDashboard(false)} />
                        </Dialog>
                    )}

                    <Tooltip title='Back' arrow>
                        <Box sx={{
                            display: "flex", alignItems: "center", gap: 1,
                            justifyContent: "flex-start"
                        }}>
                            <IconButton
                                onClick={() => navigate('/food-transport')}
                                sx={{
                                    backgroundColor: '#635bff',
                                    borderRadius: '50%',
                                    width: 35,
                                    height: 35,
                                    '&:hover': {
                                        backgroundColor: '#635bff',
                                    },
                                    boxShadow: 3,
                                }}
                            >
                                <ArrowBackIcon sx={{ color: 'white' }} />
                            </IconButton>
                        </Box>
                    </Tooltip>
                </Box>
            </Box>

            <Box className="table-grid ag-theme-alpine"
                sx={{ height: 'calc(97% - 56px)', width: '95%', margin: '0 auto', position: 'relative' }}
            >
                {isLoading ? (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 10,
                            textAlign: 'center',
                        }}
                    >
                        <CircularProgress size="3rem" sx={{ color: '#635bff' }} />
                        <Typography sx={{ marginTop: 2, color: '#635bff', fontWeight: 'bold' }}>
                            Loading tiffin orders...
                        </Typography>
                    </Box>
                ) : (
                    <AgGridReact
                        ref={gridRef}
                        rowData={rowData}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        pagination={true}
                        paginationPageSize={100}
                        onRowDoubleClicked={handleRowDoubleClick}
                        paginationPageSizeSelector={[50, 100, 200, 500]}
                        onGridReady={(params) => {
                            params.api.sizeColumnsToFit();
                        }}
                        enableCellTextSelection={true}
                        rowHeight={40}
                        headerHeight={40}
                        floatingFiltersHeight={35}
                        overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">No tiffin orders found</span>'}
                        tooltipShowDelay={0}
                        getRowStyle={(params) => ({
                            backgroundColor: params.node.rowIndex % 2 === 0 ? '#f1f5fd' : '#ffffff',
                            cursor: 'pointer'
                        })}
                    />
                )}
            </Box>
            <Box sx={{
                position: 'absolute',
                bottom: 16,
                left: 70,
                fontSize: '14px',
                color: 'black',
            }}>
                Total Orders: <b>{rowData.length}</b>
            </Box>
        </Box>
    );
};

export default TiffinTable;